import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Divider, Flex, Item, Picker, Section, TabList, TabPanels, Text } from '@adobe/react-spectrum';
import { DeepLinkTabs } from '../../../utils/deepLink/DeepLinkTabs';
import { CardTypeIcon } from '../../common/cardStats/CardTypeIcon';
import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { CardStatList } from '../../common/cardStats/CardStatList';
import { StatMetricTile } from '../../common/cardStats/StatMetricTile';
import { StatsSection } from '../../common/cardStats/StatsSection';
import { formatInt, formatNumber, formatPct } from '../../common/cardStats/formatters';
import { renderManaText } from '../../common/cardStats/manaText';
import { deriveSynergyHeadline, deriveSynergyPatternChips } from '../../../data/deckSynergyProfileMarkers';
import {
    SYNERGY_CATEGORY_LABELS as CATEGORY_LABELS,
    SYNERGY_CATEGORY_ORDER as CATEGORY_ORDER,
    SYNERGY_CATEGORY_SHORT_LABELS as CATEGORY_SHORT_LABELS,
    synergyCategoryColor as categoryColor,
} from '../../../data/synergyCategoryConstants';
import { SynergyFingerprint } from './SynergyFingerprint';
import { SynergyWebModal } from './SynergyWebModal';

import './SynergyPane.css';
import './mana-cost.css';

/**
 * Soft-deny list shared with the commander synergy pane — these cards /
 * card categories anchor high-scoring entries but don't represent
 * interesting synergy signal (they're just staples). Categories are
 * lowercased at compare time because the backend casing isn't consistent.
 */
const EXCLUDED_ANCHOR_CARD_IDS = new Set(['chrome_mox', 'mox_diamond']);
const EXCLUDED_ANCHOR_CATEGORIES = new Set(['counterspell', 'spotremoval', 'boardwipes']);

function shouldExcludeAnchor(cardId, cardMeta) {
    if (!cardId) return false;
    if (EXCLUDED_ANCHOR_CARD_IDS.has(cardId)) return true;
    const cats = Array.isArray(cardMeta?.categories) ? cardMeta.categories : [];
    return cats.some((c) => EXCLUDED_ANCHOR_CATEGORIES.has((c || '').toLowerCase()));
}

const TYPE_GROUPS = [
    { key: 'commanders', label: 'Commanders', match: (c) => c?.isCommander },
    {
        key: 'planeswalkers',
        label: 'Planeswalkers',
        match: (c) => (c?.types || '').toLowerCase().includes('planeswalker'),
    },
    {
        key: 'creatures',
        label: 'Creatures',
        match: (c) => (c?.types || '').toLowerCase().includes('creature'),
    },
    {
        key: 'artifacts',
        label: 'Artifacts',
        match: (c) => (c?.types || '').toLowerCase().includes('artifact'),
    },
    {
        key: 'enchantments',
        label: 'Enchantments',
        match: (c) => (c?.types || '').toLowerCase().includes('enchantment'),
    },
    {
        key: 'instants',
        label: 'Instants',
        match: (c) => (c?.types || '').toLowerCase().includes('instant'),
    },
    {
        key: 'sorceries',
        label: 'Sorceries',
        match: (c) => (c?.types || '').toLowerCase().includes('sorcery'),
    },
    {
        key: 'battles',
        label: 'Battles',
        match: (c) => (c?.types || '').toLowerCase().includes('battle'),
    },
    {
        key: 'lands',
        label: 'Lands',
        match: (c) => (c?.types || '').toLowerCase().includes('land'),
    },
];

const prettyFormatEffectText = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
};

function sourceToCard(source, cards) {
    if (!source) return null;
    const id = typeof source === 'string' ? source : source.id;
    if (!id) return null;
    const card = cards?.[id];
    if (!card) return null;
    const supportText = typeof source === 'object' ? source?.supportCondition?.text : null;
    return {
        ...card,
        id,
        extraData: supportText ? `"${supportText}"` : card?.extraData,
    };
}

function extractCardList(rawList, cards) {
    if (!Array.isArray(rawList)) return [];
    const seen = new Set();
    const out = [];
    rawList.forEach((src) => {
        const card = sourceToCard(src, cards);
        if (!card || seen.has(card.id)) return;
        seen.add(card.id);
        out.push(card);
    });
    return out;
}

export function SynergyPane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const synergyList = deck?.details?.synergy?.list || {};
    const totalScore = deck?.details?.synergy?.scoring?.totalScore || 0;
    const profile = deck?.details?.synergy?.profile || null;

    const openCardInfo = useOpenCardInfo();
    const handleSelectCard = (card) => {
        if (!card) return;
        openCardInfo(card);
    };

    const [fingerprintOpen, setFingerprintOpen] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
        return window.matchMedia('(min-width: 768px)').matches;
    });
    const [webModalOpen, setWebModalOpen] = useState(false);

    const headline = useMemo(() => deriveSynergyHeadline(profile), [profile]);
    const patternChips = useMemo(() => deriveSynergyPatternChips(profile), [profile]);

    const deckStats = useMemo(() => {
        const counts = { triggers: 0, abilities: 0, statics: 0, replacements: 0 };
        const anchorByCard = new Map();
        const allEntries = [];
        let entryCount = 0;
        let cardCount = 0;
        let totalSupporters = 0;
        let totalEnablers = 0;

        Object.keys(synergyList).forEach((cardId) => {
            const node = synergyList[cardId];
            if (!node) return;
            let touched = false;
            CATEGORY_ORDER.forEach((cat) => {
                const bucket = node[cat] || {};
                Object.keys(bucket).forEach((entryKey) => {
                    const entry = bucket[entryKey];
                    if (!entry) return;
                    touched = true;
                    counts[cat] += 1;
                    entryCount += 1;
                    const supporters = entry?.sources || [];
                    const enablers = entry?.enablers || [];
                    totalSupporters += supporters.length;
                    totalEnablers += enablers.length;
                    const score = Number(entry?.conditionScoring?.total || 0);
                    if (!anchorByCard.has(cardId)) {
                        anchorByCard.set(cardId, { cardId, score: 0, entries: 0, supporters: 0 });
                    }
                    const anchor = anchorByCard.get(cardId);
                    anchor.entries += 1;
                    anchor.score += score;
                    anchor.supporters += supporters.length;
                    allEntries.push({
                        cardId,
                        category: cat,
                        entryKey,
                        text: entry?.text,
                        score,
                        supportersCount: supporters.length,
                        enablersCount: enablers.length,
                    });
                });
            });
            if (touched) cardCount += 1;
        });

        const topAnchors = Array.from(anchorByCard.values())
            .filter((a) => !shouldExcludeAnchor(a.cardId, cards?.[a.cardId]))
            .map((a) => {
                const c = cards?.[a.cardId];
                return {
                    ...a,
                    name: c?.name || a.cardId,
                    imageUri: c?.imageUri,
                    types: c?.types,
                };
            })
            .sort((a, b) => b.score - a.score)
            .slice(0, 10);

        const topSynergies = allEntries
            .filter((e) => !shouldExcludeAnchor(e.cardId, cards?.[e.cardId]))
            .sort((a, b) => b.score - a.score)
            .slice(0, 15)
            .map((e) => {
                const c = cards?.[e.cardId];
                return {
                    ...e,
                    name: c?.name || e.cardId,
                    imageUri: c?.imageUri,
                    types: c?.types,
                };
            });

        return {
            counts,
            entryCount,
            cardCount,
            totalSupporters,
            totalEnablers,
            avgEntriesPerCard: cardCount > 0 ? entryCount / cardCount : 0,
            avgSupportersPerEntry: entryCount > 0 ? totalSupporters / entryCount : 0,
            avgEnablersPerEntry: entryCount > 0 ? totalEnablers / entryCount : 0,
            topAnchors,
            topSynergies,
        };
    }, [synergyList, cards]);

    const entryCategoryData = useMemo(() => {
        const total = CATEGORY_ORDER.reduce((sum, k) => sum + (deckStats.counts[k] || 0), 0);
        if (total <= 0) return [];
        return CATEGORY_ORDER.filter((k) => (deckStats.counts[k] || 0) > 0).map((k) => ({
            key: k,
            label: CATEGORY_SHORT_LABELS[k],
            color: categoryColor(k),
            count: deckStats.counts[k],
            share: deckStats.counts[k] / total,
        }));
    }, [deckStats.counts]);

    const cardMenu = useMemo(() => {
        const groups = TYPE_GROUPS.map((g) => ({ ...g, items: [] }));
        Object.keys(synergyList).forEach((id) => {
            const card = cards?.[id];
            if (!card) return;
            const group = groups.find((g) => g.match(card)) || groups[groups.length - 1];
            group.items.push({ id, name: card?.name || id });
        });
        return groups
            .filter((g) => g.items.length > 0)
            .map((g) => ({
                id: g.key,
                name: g.label,
                children: g.items.sort((a, b) => (a.name || '').localeCompare(b.name || '')),
            }));
    }, [synergyList, cards]);

    const firstCardId = cardMenu[0]?.children?.[0]?.id || null;
    const [selectedCardId, setSelectedCardId] = useState(firstCardId);

    useEffect(() => {
        if (!selectedCardId && firstCardId) setSelectedCardId(firstCardId);
    }, [firstCardId, selectedCardId]);

    const effectMenu = useMemo(() => {
        if (!selectedCardId) return [];
        const node = synergyList[selectedCardId] || {};
        return CATEGORY_ORDER.filter((cat) => node[cat] && Object.keys(node[cat]).length > 0).map((cat) => ({
            id: cat,
            name: CATEGORY_LABELS[cat],
            children: Object.keys(node[cat]).map((entryKey) => {
                const entry = node[cat][entryKey];
                return {
                    id: `${cat}::${entryKey}`,
                    category: cat,
                    entryKey,
                    name: prettyFormatEffectText(entry?.text) || entryKey,
                    text: entry?.text,
                };
            }),
        }));
    }, [synergyList, selectedCardId]);

    const flatEffects = useMemo(() => effectMenu.flatMap((section) => section.children), [effectMenu]);
    const [selectedEffectId, setSelectedEffectId] = useState(flatEffects[0]?.id || null);

    useEffect(() => {
        if (!flatEffects.length) {
            setSelectedEffectId(null);
            return;
        }
        if (!selectedEffectId || !flatEffects.find((e) => e.id === selectedEffectId)) {
            setSelectedEffectId(flatEffects[0].id);
        }
    }, [flatEffects, selectedEffectId]);

    const selectedEffect = flatEffects.find((e) => e.id === selectedEffectId) || flatEffects[0];
    const rawEntry = selectedEffect
        ? synergyList?.[selectedCardId]?.[selectedEffect.category]?.[selectedEffect.entryKey]
        : null;

    const groupOptions = useMemo(() => {
        if (!rawEntry) return [];
        const defs = [
            { key: 'sources', label: 'Triggers', source: rawEntry?.sources },
            { key: 'enablers', label: 'Enablers', source: rawEntry?.enablers },
            {
                key: 'cardsOfSupportingType',
                label: 'Cards of supporting type',
                source: rawEntry?.cardsOfSupportingType,
            },
            {
                key: 'cardsOfSupportingColor',
                label: 'Cards of supporting color',
                source: rawEntry?.cardsOfSupportingColor,
            },
            { key: 'targets', label: 'Targets', source: rawEntry?.targets },
            { key: 'replacements', label: 'Replacements', source: rawEntry?.replacements },
            {
                key: 'textPartCardsOfSupportingType',
                label: 'Text-part type matches',
                source: rawEntry?.textPartCardsOfSupportingType,
            },
        ];
        return defs
            .map((d) => ({ ...d, cards: extractCardList(d.source, cards) }))
            .filter((d) => d.cards.length > 0)
            .map((d) => ({
                key: d.key,
                label: `${d.label} (${d.cards.length})`,
                cards: d.cards,
            }));
    }, [rawEntry, cards]);

    const [selectedGroup, setSelectedGroup] = useState(groupOptions[0]?.key || null);

    useEffect(() => {
        if (!groupOptions.length) {
            setSelectedGroup(null);
            return;
        }
        if (!groupOptions.find((o) => o.key === selectedGroup)) {
            setSelectedGroup(groupOptions[0].key);
        }
    }, [groupOptions, selectedGroup]);

    const selectedCard = selectedCardId ? cards?.[selectedCardId] : null;
    const currentGroup = groupOptions.find((g) => g.key === selectedGroup) || groupOptions[0];
    const currentCards = currentGroup?.cards || [];

    if (deckStats.entryCount === 0) {
        return (
            <Flex direction="column" UNSAFE_className="synergyPane" padding="12px">
                <Text UNSAFE_className="commanderCardStatsEmpty">No synergy entries found for this deck yet.</Text>
            </Flex>
        );
    }

    const displayScore = profile?.score != null ? profile.score : totalScore;
    const displayEntries = profile?.entryCount != null ? profile.entryCount : deckStats.entryCount;
    const displayParticipating = profile?.participatingCards != null ? profile.participatingCards : deckStats.cardCount;
    const displayAvgSupporters =
        profile?.avgSupportersPerEntry != null ? profile.avgSupportersPerEntry : deckStats.avgSupportersPerEntry;
    const displayAvgEnablers =
        profile?.avgEnablersPerEntry != null ? profile.avgEnablersPerEntry : deckStats.avgEnablersPerEntry;

    const coverageHint =
        profile?.coverage != null
            ? `${formatPct(profile.coverage)} coverage`
            : `across ${displayParticipating} synergy cards`;

    const primaryPattern = patternChips[0];
    const secondaryPatterns = patternChips.slice(1);
    const synergyTitle = primaryPattern?.label || 'Synergy Web';
    const synergyTone = primaryPattern?.tone || 'cool';

    return (
        <Flex direction="column" UNSAFE_className="synergyPane" rowGap={20} padding="12px">
            <section className={`synergyHero synergyHero--${synergyTone}`} data-tone={synergyTone}>
                <div className="synergyHeroTop">
                    <div className="synergyHeroMain">
                        <span className="synergyHeroEyebrow">Synergy Connections</span>
                        <span className="synergyHeroName">{synergyTitle}</span>
                        {headline && <span className="synergyHeroHint">{headline}</span>}
                        {secondaryPatterns.length > 0 && (
                            <div className="synergyHeroChips">
                                {secondaryPatterns.map((chip) => (
                                    <span key={chip.key} className={`synergyHeroChip synergyHeroChip--${chip.tone}`}>
                                        <span className="synergyHeroChipValue">{chip.label}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="synergyHeroSide">
                        <div className="synergyHeroTotalBox">
                            <span className="synergyHeroTotalLabel">Synergy score</span>
                            <span className="synergyHeroTotalValue">{formatInt(displayScore)}</span>
                            <span className="synergyHeroTotalUnit">{coverageHint}</span>
                        </div>
                    </div>
                </div>
            </section>

            {(profile || headline || patternChips.length > 0) && (
                <section className={`powerSection powerSection--collapsible${fingerprintOpen ? ' is-open' : ''}`}>
                    <button
                        type="button"
                        className="powerSectionToggle"
                        aria-expanded={fingerprintOpen}
                        aria-controls="synergyFingerprintBody"
                        onClick={() => setFingerprintOpen((v) => !v)}
                    >
                        <span className="powerSectionEyebrow">
                            <span className="powerSectionChevron" aria-hidden="true">
                                {fingerprintOpen ? '▾' : '▸'}
                            </span>
                            Synergy profile
                        </span>
                        <span className="powerSectionHint">
                            Mechanical shape of the synergy web — category mix, concentration, commander centricity, and
                            the cards carrying the plan.
                        </span>
                    </button>

                    {fingerprintOpen && (
                        <div id="synergyFingerprintBody" className="powerSectionBody">
                            <div className="statsMetricsGrid">
                                <StatMetricTile
                                    label="Entries"
                                    value={formatInt(displayEntries)}
                                    hint={`across ${displayParticipating} cards`}
                                />
                                <StatMetricTile
                                    label="Supporters / entry"
                                    value={formatNumber(displayAvgSupporters, 1)}
                                    hint={`${formatInt(deckStats.totalSupporters)} total`}
                                />
                                <StatMetricTile
                                    label="Enablers / entry"
                                    value={formatNumber(displayAvgEnablers, 1)}
                                    hint={`${formatInt(deckStats.totalEnablers)} total`}
                                />
                            </div>
                            <SynergyFingerprint
                                profile={profile}
                                cards={cards}
                                onSelectCard={handleSelectCard}
                                onOpenWeb={() => setWebModalOpen(true)}
                            />
                        </div>
                    )}
                </section>
            )}

            <SynergyWebModal
                isOpen={webModalOpen}
                onClose={() => setWebModalOpen(false)}
                synergyList={synergyList}
                cards={cards}
                profile={profile}
            />

            {!profile && entryCategoryData.length > 0 && (
                <StatsSection
                    title="Entry density by category"
                    subtitle="Where synergy entries live — triggered vs activated vs static vs replacement"
                >
                    <div className="synergyStackedBar">
                        {entryCategoryData.map((segment) => (
                            <div
                                key={segment.key}
                                className="synergyStackedSegment"
                                style={{
                                    width: `${Math.max(segment.share * 100, 2)}%`,
                                    background: segment.color,
                                }}
                                title={`${segment.label} — ${segment.count} entries`}
                            />
                        ))}
                    </div>
                    <div className="synergyStackedLegend">
                        {entryCategoryData.map((segment) => (
                            <div key={segment.key} className="synergyStackedLegendItem">
                                <span className="synergyStackedSwatch" style={{ background: segment.color }} />
                                <span className="synergyStackedLegendLabel">{segment.label}</span>
                                <span className="synergyStackedLegendValue">{segment.count}</span>
                            </div>
                        ))}
                    </div>
                </StatsSection>
            )}

            <DeepLinkTabs defaultSelectedKey="drilldown" isEmphasized UNSAFE_className="synergySubTabs">
                <TabList>
                    <Item key="drilldown">Drilldown</Item>
                    <Item key="top">Top synergies</Item>
                </TabList>
                <TabPanels>
                    <Item key="drilldown">
                        <StatsSection
                            title="Effect drilldown"
                            subtitle="Pick a card and one of its effects to see every trigger + enabler in this deck"
                        >
                            <Flex
                                direction="column"
                                alignItems="center"
                                justifyContent="center"
                                rowGap={12}
                                UNSAFE_className="synergyHeaderBlock"
                            >
                                {selectedCard?.imageUri && (
                                    <img
                                        src={selectedCard.imageUri}
                                        alt={selectedCard.name}
                                        className="deckSynergyCardImage"
                                        onClick={() => handleSelectCard(selectedCard)}
                                    />
                                )}
                                <div className="synergyPickerRow">
                                    <Text UNSAFE_className="synergyMenuBoxLabel">CARD:</Text>
                                    <Picker
                                        aria-label="Card"
                                        UNSAFE_className="synergyPicker"
                                        selectedKey={selectedCardId}
                                        onSelectionChange={(key) => key && setSelectedCardId(key)}
                                        items={cardMenu}
                                        width="100%"
                                    >
                                        {(section) => (
                                            <Section items={section.children} title={section.name}>
                                                {(item) => <Item key={item.id}>{item.name}</Item>}
                                            </Section>
                                        )}
                                    </Picker>
                                </div>
                                {flatEffects.length > 0 && (
                                    <div className="synergyPickerRow">
                                        <Text UNSAFE_className="synergyMenuBoxLabel">EFFECT:</Text>
                                        <Picker
                                            aria-label="Effect"
                                            UNSAFE_className="synergyPicker"
                                            selectedKey={selectedEffectId}
                                            onSelectionChange={(key) => key && setSelectedEffectId(key)}
                                            items={effectMenu}
                                            width="100%"
                                        >
                                            {(section) => (
                                                <Section items={section.children} title={section.name}>
                                                    {(item) => (
                                                        <Item key={item.id} textValue={item.name}>
                                                            <Text>{renderManaText(item.name)}</Text>
                                                        </Item>
                                                    )}
                                                </Section>
                                            )}
                                        </Picker>
                                    </div>
                                )}
                            </Flex>

                            <Divider size="S" />

                            {groupOptions.length === 0 ? (
                                <Text UNSAFE_className="commanderCardStatsEmpty">
                                    No supporting cards recorded for this effect.
                                </Text>
                            ) : (
                                <Flex direction="column" rowGap={10}>
                                    <div className="synergyPickerRow">
                                        <Text UNSAFE_className="synergyMenuBoxLabel">GROUP:</Text>
                                        <Picker
                                            aria-label="Card group"
                                            UNSAFE_className="synergyPicker"
                                            selectedKey={selectedGroup}
                                            onSelectionChange={setSelectedGroup}
                                            items={groupOptions}
                                            width="100%"
                                            isDisabled={groupOptions.length < 2}
                                        >
                                            {(option) => <Item key={option.key}>{option.label}</Item>}
                                        </Picker>
                                    </div>
                                    <div className="synergyCardListBlock synergyCardListScroll">
                                        <CardStatList
                                            cards={currentCards}
                                            deckCount={1}
                                            onSelectCard={handleSelectCard}
                                            resetKey={`deckSynergy:${selectedCardId}:${selectedEffectId}:${selectedGroup}`}
                                            initialVisible={60}
                                            compactFilters
                                            emptyMessageUnfiltered="No cards recorded for this effect."
                                            renderCard={(card) => (
                                                <DeckSynergyCardRow
                                                    key={card.id}
                                                    card={card}
                                                    onSelectCard={handleSelectCard}
                                                />
                                            )}
                                        />
                                    </div>
                                </Flex>
                            )}
                        </StatsSection>
                    </Item>
                    <Item key="top">
                        {deckStats.topSynergies.length > 0 ? (
                            <StatsSection
                                title="Top synergies"
                                subtitle="Individual effects ranked by score contribution"
                            >
                                <Flex direction="column" rowGap={6}>
                                    {deckStats.topSynergies.map((synergy) => (
                                        <DeckTopSynergyRow
                                            key={`${synergy.cardId}::${synergy.category}::${synergy.entryKey}`}
                                            synergy={synergy}
                                            onSelectCard={handleSelectCard}
                                        />
                                    ))}
                                </Flex>
                            </StatsSection>
                        ) : (
                            <Text UNSAFE_className="commanderCardStatsEmpty">
                                No top synergies recorded for this deck yet.
                            </Text>
                        )}
                    </Item>
                </TabPanels>
            </DeepLinkTabs>
        </Flex>
    );
}

function DeckSynergyCardRow({ card, onSelectCard }) {
    const handleClick = () => {
        if (card?.imageUri) onSelectCard?.(card);
    };
    return (
        <Flex direction="row" alignItems="center" columnGap={10} UNSAFE_className="synergyCardRow">
            {card?.imageUri ? (
                <img
                    src={card.imageUri}
                    alt={card.name}
                    className="cardStatThumb"
                    onClick={handleClick}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <CardTypeIcon types={card?.types} />
            <Flex direction="column" flex={1} minWidth={0}>
                <span
                    className="synergyCardRowName"
                    onClick={handleClick}
                    style={{ cursor: card?.imageUri ? 'pointer' : 'default' }}
                    title={card?.name}
                >
                    {card?.name}
                </span>
                {card?.extraData && <span className="synergyCardRowText">{renderManaText(card.extraData)}</span>}
            </Flex>
        </Flex>
    );
}

function DeckTopSynergyRow({ synergy, onSelectCard }) {
    const handleClick = () => {
        if (synergy?.imageUri) onSelectCard?.({ imageUri: synergy.imageUri, name: synergy.name });
    };
    const catColor = categoryColor(synergy.category);
    return (
        <div className="topSynergyRow">
            {synergy?.imageUri ? (
                <img
                    src={synergy.imageUri}
                    alt={synergy.name}
                    className="cardStatThumb"
                    onClick={handleClick}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <div className="topSynergyBody">
                <div className="topSynergyHeader">
                    <span className="topSynergyCategory" style={{ borderLeftColor: catColor, color: catColor }}>
                        {CATEGORY_SHORT_LABELS[synergy.category] || synergy.category}
                    </span>
                    <span
                        className="topSynergyName"
                        onClick={handleClick}
                        style={{ cursor: synergy?.imageUri ? 'pointer' : 'default' }}
                    >
                        {synergy.name}
                    </span>
                </div>
                {synergy?.text && (
                    <span className="topSynergyText">
                        &ldquo;{renderManaText(prettyFormatEffectText(synergy.text))}&rdquo;
                    </span>
                )}
            </div>
            <div className="topSynergyMeta">
                <span className="topSynergyScore" title="Synergy score contribution for this effect">
                    {formatInt(synergy.score)}
                </span>
                <span className="topSynergyCount" title="Supporters / enablers on this effect">
                    {`${synergy.supportersCount}s / ${synergy.enablersCount}e`}
                </span>
            </div>
        </div>
    );
}
